/**
 * Vendored from `tamoomeh-service/packages/contracts/src/customer-services.ts`.
 *
 * The Tamoomeh Core API validates a customer request against the same catalog this
 * widget renders, so the two must agree. That copy is canonical; this one is a
 * byte-for-byte copy with only this header added, and `npm run check:contracts`
 * fails when they diverge. Edit Tamoomeh's copy and re-copy, never this file.
 */

import type { PorabzarPageKind, ServiceSlug } from './services.js';

/**
 * The Tamoomeh customer-facing service catalog.
 *
 * Tamoomeh (`tamoomeh.ir`) presents three customer sections — وکیل، مربی،
 * مشاوره کودک — and each one is fulfilled by one of the Tamoomeh domain services.
 * This file is the single place where those two vocabularies meet, and it is
 * deliberately runtime-dependency-free (the only import is type-only) so that a
 * browser can load it directly without dragging the rest of the contracts
 * package in.
 *
 * Why the two vocabularies are separate:
 *  - `vakil` / `morabi` / `koodak` are *customer-facing section* identifiers.
 *    They are URL segments and analytics keys, chosen for a Persian audience.
 *  - `barachi` / `mamad` / `symphonic` are the *backend service* identifiers.
 *    They are database keys, JWT audiences and internal API prefixes, and the
 *    README promises they never change.
 *
 * Renaming one must not rename the other, so the mapping lives here and nowhere
 * else. Every service-specific difference the request widget needs — the label,
 * the theme, the form fields, the price, the backend that fulfils it — is data
 * in this table. There is no `if (service === 'vakil')` anywhere in the client.
 */

export const CUSTOMER_SERVICE_SLUGS = ['vakil', 'morabi', 'koodak'] as const;
export type CustomerServiceSlug = (typeof CUSTOMER_SERVICE_SLUGS)[number];

/**
 * Theme identity for a customer section.
 *
 * Values are raw CSS colours rather than a class name so the same definition
 * works in the shadow DOM of the embeddable widget, in the server-rendered
 * Tamoomeh pages, and in the Porabzar embed — three very different style
 * scopes that cannot share a stylesheet selector.
 */
export interface CustomerServiceTheme {
  /** Stable token, also emitted as `data-theme` for CSS hooks. */
  token: string;
  /** Primary accent. */
  accent: string;
  /** Darker accent for hover/active and for accent-coloured text on light. */
  accentStrong: string;
  /** Very light tint used for panel backgrounds. */
  accentSoft: string;
  /** Text colour that is readable on `accent`. */
  onAccent: string;
}

/** The body-photo views a sport intake may attach. */
export const CUSTOMER_PHOTO_KINDS = ['FRONT', 'BACK', 'SIDE'] as const;
export type CustomerPhotoKind = (typeof CUSTOMER_PHOTO_KINDS)[number];

/** A form field, described as data so the widget renders it generically. */
export interface CustomerServiceField {
  name: string;
  labelFa: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'number' | 'file';
  required: boolean;
  placeholderFa?: string;
  helpFa?: string;
  maxLength?: number;
  options?: readonly { value: string; labelFa: string }[];
  /**
   * Inclusive bounds for `type: 'number'`, in the field's own unit (years,
   * centimetres, kilograms). The widget uses them for the input's `min`/`max`
   * and the answer validator enforces the same range, so the browser hint and
   * the accepted value cannot disagree.
   */
  min?: number;
  max?: number;
  /** The stepper increment for `type: 'number'`. */
  step?: number;
  /**
   * For `type: 'file'`: the `accept` attribute and the photo view this field
   * uploads as. A file field's value never enters `answers` — the bytes go to
   * the upload endpoint, not the JSON draft.
   */
  accept?: string;
  photoKind?: CustomerPhotoKind;
}

export interface CustomerServiceDefinition {
  /** Customer-facing section key; URL segment and analytics key. */
  slug: CustomerServiceSlug;
  /** Persian display name, e.g. «وکیل». */
  nameFa: string;
  /** Latin name, for logs and non-Persian contexts. Never shown as a URL. */
  nameEn: string;
  /** Section landing route, e.g. `/vakil`. */
  route: string;
  /** Request route, e.g. `/vakil/request`. */
  requestRoute: string;
  /** The Tamoomeh domain service that fulfils requests for this section. */
  coreServiceSlug: ServiceSlug;
  /** `<title>` fragment for the landing page. */
  titleFa: string;
  /** Meta description for the landing page. */
  descriptionFa: string;
  /** One-line promise shown next to the request CTA. */
  summaryFa: string;
  /** Content category this section's articles belong to. */
  contentCategory: string;
  theme: CustomerServiceTheme;
  /** Icon key understood by the shared widget's inline icon set. */
  icon: string;
  /**
   * Server-authoritative default price in IRR, used when the service registry
   * row carries no `metadata.defaultAmount`. The browser never sends a price:
   * Core resolves it from this table so a widget cannot choose what to charge.
   */
  defaultAmount: number;
  /**
   * The list price before the discount, in IRR, or `undefined` when the section
   * is not on offer.
   *
   * Presentation only: the payable amount is always `defaultAmount` (or the
   * registry row's `metadata.defaultAmount`), so neither this field nor a value
   * typed into a form can change what a customer is charged. The percentage is
   * derived from the two numbers, never stored, so a price change cannot leave
   * a stale «۵۰٪ تخفیف» on the page: 1,500,000 → 1,000,000 is a third off.
   */
  listAmount?: number;
  currency: 'IRR';
  /** Call-to-action label, e.g. «درخواست مشاوره حقوقی». */
  ctaFa: string;
  /** Label for the request page heading. */
  requestTitleFa: string;
  /**
   * Session duration shown directly beneath the price, e.g. «۳۰ دقیقه».
   * Presentation only: it never affects pricing or payment logic.
   */
  durationFa?: string;
  /** Fields the request form renders, in order. */
  fields: readonly CustomerServiceField[];
  /** Porabzar page kinds that should surface this section's widget. */
  porabzarPageKinds: readonly PorabzarPageKind[];
}

/** Shared tail of every request form: one free-text subject and one body. */
const SUBJECT_FIELD: CustomerServiceField = {
  name: 'subject',
  labelFa: 'موضوع درخواست',
  type: 'text',
  required: true,
  placeholderFa: 'در یک خط بنویسید چه می‌خواهید',
  maxLength: 120,
};

const DETAILS_FIELD: CustomerServiceField = {
  name: 'details',
  labelFa: 'توضیح بیشتر',
  type: 'textarea',
  required: true,
  placeholderFa: 'جزئیات را بنویسید تا کارشناس دقیق‌تر پاسخ دهد',
  helpFa: 'هرچه دقیق‌تر بنویسید، پاسخ سریع‌تر و مرتبط‌تر خواهد بود.',
  maxLength: 2000,
};

export const CUSTOMER_SERVICE_DEFINITIONS: readonly CustomerServiceDefinition[] = [
  {
    slug: 'vakil',
    nameFa: 'وکیل',
    nameEn: 'vakil',
    route: '/vakil',
    requestRoute: '/vakil/request',
    coreServiceSlug: 'barachi',
    titleFa: 'وکیل و مشاوره حقوقی',
    descriptionFa:
      'راهنمای حقوقی به زبان ساده و درخواست مشاوره از وکیل؛ قرارداد، ملک، خانواده، کسب‌وکار و دعاوی.',
    summaryFa: 'موضوع حقوقی‌تان را بنویسید؛ وکیل بررسی می‌کند و پاسخ می‌دهد.',
    contentCategory: 'legal',
    theme: {
      token: 'vakil',
      accent: '#1d4ed8',
      accentStrong: '#1e3a8a',
      accentSoft: '#eff4ff',
      onAccent: '#ffffff',
    },
    icon: 'scale',
    // 1,000,000 toman on offer (rial is the stored unit: 10,000,000 IRR), down
    // from a 1,500,000-toman list price (15,000,000 IRR) — a third off.
    defaultAmount: 10_000_000,
    listAmount: 15_000_000,
    currency: 'IRR',
    ctaFa: 'درخواست مشاوره حقوقی',
    requestTitleFa: 'درخواست مشاوره حقوقی',
    durationFa: '۳۰ دقیقه',
    fields: [
      SUBJECT_FIELD,
      {
        name: 'area',
        labelFa: 'حوزه حقوقی',
        type: 'select',
        required: true,
        options: [
          { value: 'family', labelFa: 'خانواده و ارث' },
          { value: 'property', labelFa: 'ملک و قرارداد' },
          { value: 'business', labelFa: 'کسب‌وکار و شرکت' },
          { value: 'criminal', labelFa: 'کیفری' },
          { value: 'other', labelFa: 'موضوع دیگر' },
        ],
      },
      DETAILS_FIELD,
    ],
    porabzarPageKinds: ['law'],
  },
  {
    slug: 'morabi',
    nameFa: 'مربی',
    nameEn: 'morabi',
    route: '/morabi',
    requestRoute: '/morabi/request',
    coreServiceSlug: 'mamad',
    titleFa: 'مربی و برنامه ورزشی',
    descriptionFa:
      'برنامه تمرینی و تغذیه، انتخاب مربی و پیگیری پیشرفت؛ برای مبتدی تا حرفه‌ای.',
    summaryFa: 'هدف ورزشی‌تان را بگویید؛ مربی برنامه اختصاصی می‌نویسد.',
    contentCategory: 'sports',
    theme: {
      token: 'morabi',
      accent: '#0f766e',
      accentStrong: '#115e59',
      accentSoft: '#effcf9',
      onAccent: '#ffffff',
    },
    icon: 'dumbbell',
    // Same offer as وکیل and مشاوره کودک: 1,000,000 toman from 1,500,000.
    defaultAmount: 10_000_000,
    listAmount: 15_000_000,
    currency: 'IRR',
    ctaFa: 'درخواست برنامه و مربی',
    requestTitleFa: 'درخواست برنامه ورزشی',
    fields: [
      SUBJECT_FIELD,
      {
        name: 'goal',
        labelFa: 'هدف اصلی',
        type: 'select',
        required: true,
        options: [
          { value: 'weight-loss', labelFa: 'کاهش وزن' },
          { value: 'muscle', labelFa: 'عضله‌سازی' },
          { value: 'fitness', labelFa: 'آمادگی عمومی' },
          { value: 'rehab', labelFa: 'بازتوانی و اصلاح حرکات' },
          { value: 'other', labelFa: 'هدف دیگر' },
        ],
      },
      {
        name: 'level',
        labelFa: 'سطح فعلی',
        type: 'select',
        required: true,
        options: [
          { value: 'beginner', labelFa: 'مبتدی' },
          { value: 'intermediate', labelFa: 'متوسط' },
          { value: 'advanced', labelFa: 'پیشرفته' },
        ],
      },
      // The coach cannot write a safe programme without these four. They are
      // numbers with the same bounds Mamad's own intake schema uses, and the
      // widget renders them as decimal text inputs (Persian digits allowed).
      {
        name: 'age',
        labelFa: 'سن (سال)',
        type: 'number',
        required: true,
        min: 10,
        max: 100,
        step: 1,
        placeholderFa: 'مثلاً ۲۸',
      },
      {
        name: 'height',
        labelFa: 'قد (سانتی‌متر)',
        type: 'number',
        required: true,
        min: 80,
        max: 260,
        step: 1,
        placeholderFa: 'مثلاً ۱۷۵',
      },
      {
        name: 'weight',
        labelFa: 'وزن (کیلوگرم)',
        type: 'number',
        required: true,
        min: 25,
        max: 400,
        step: 0.5,
        placeholderFa: 'مثلاً ۷۲٫۵',
      },
      // Medical history is asked for but never required: "none" is a valid and
      // common answer, and blocking a request on it would lose the customer.
      {
        name: 'diseases',
        labelFa: 'بیماری‌ها و سابقه پزشکی',
        type: 'textarea',
        required: false,
        maxLength: 1000,
        placeholderFa: 'مثلاً: فشار خون، دیسک کمر، سابقه جراحی',
        helpFa: 'اگر بیماری یا سابقه پزشکی مهمی دارید بنویسید؛ در غیر این صورت خالی بگذارید.',
      },
      {
        name: 'medicines',
        labelFa: 'داروهای مصرفی',
        type: 'textarea',
        required: false,
        maxLength: 1000,
        placeholderFa: 'مثلاً: متفورمین، لوزارتان',
        helpFa: 'داروهایی که به‌طور منظم مصرف می‌کنید؛ اگر دارویی مصرف نمی‌کنید خالی بگذارید.',
      },
      DETAILS_FIELD,
      {
        name: 'wantsCall',
        labelFa: 'می‌خواهم مربی با من تماس بگیرد',
        type: 'checkbox',
        required: false,
        helpFa: 'اگر این گزینه را انتخاب کنید، مربی پیش از نوشتن برنامه با شما تماس می‌گیرد.',
      },
      // The optional photo section. Three separate fields so the generic renderer
      // needs no special case: each one is a file input, and `photoKind` is the
      // view the bytes are filed under.
      {
        name: 'photoFront',
        labelFa: 'تصویر بدن — روبه‌رو',
        type: 'file',
        required: false,
        accept: 'image/*',
        photoKind: 'FRONT',
        helpFa: 'اختیاری؛ به مربی کمک می‌کند وضعیت بدن شما را دقیق‌تر ببیند.',
      },
      {
        name: 'photoBack',
        labelFa: 'تصویر بدن — پشت',
        type: 'file',
        required: false,
        accept: 'image/*',
        photoKind: 'BACK',
        helpFa: 'اختیاری.',
      },
      {
        name: 'photoSide',
        labelFa: 'تصویر بدن — از پهلو',
        type: 'file',
        required: false,
        accept: 'image/*',
        photoKind: 'SIDE',
        helpFa: 'اختیاری.',
      },
    ],
    porabzarPageKinds: ['sports'],
  },
  {
    slug: 'koodak',
    nameFa: 'مشاوره کودک',
    nameEn: 'koodak',
    route: '/koodak',
    requestRoute: '/koodak/request',
    coreServiceSlug: 'symphonic',
    titleFa: 'مشاوره کودک و فرزندپروری',
    descriptionFa:
      'راهنمای فرزندپروری و رشد کودک، و درخواست مشاوره از کارشناس کودک؛ رفتار، اضطراب، تحصیل و خواب.',
    summaryFa: 'دربارهٔ کودک‌تان بنویسید؛ کارشناس کودک بررسی می‌کند و پاسخ می‌دهد.',
    contentCategory: 'child',
    theme: {
      token: 'koodak',
      accent: '#15803d',
      accentStrong: '#166534',
      accentSoft: '#f0fdf4',
      onAccent: '#ffffff',
    },
    icon: 'child',
    // Same offer as وکیل and مربی.
    defaultAmount: 10_000_000,
    listAmount: 15_000_000,
    currency: 'IRR',
    ctaFa: 'درخواست مشاوره کودک',
    requestTitleFa: 'درخواست مشاوره کودک',
    durationFa: '۱ ساعت',
    fields: [
      SUBJECT_FIELD,
      {
        name: 'ageGroup',
        labelFa: 'گروه سنی کودک',
        type: 'select',
        required: true,
        // Two bands, exactly as the child-consultation service works them:
        // a toddler is up to four, a child is five to twelve. The backend still
        // accepts the legacy `TEEN` value so old requests keep a label, but the
        // form no longer offers it.
        options: [
          { value: 'toddler', labelFa: 'تا ۴ سال - نوپا' },
          { value: 'school', labelFa: '۵ تا ۱۲ سال - کودک' },
        ],
      },
      {
        name: 'topic',
        labelFa: 'موضوع مشاوره',
        type: 'select',
        required: true,
        options: [
          { value: 'behavior', labelFa: 'رفتار و تربیت' },
          { value: 'anxiety', labelFa: 'اضطراب و ترس' },
          { value: 'school', labelFa: 'تحصیل و تمرکز' },
          { value: 'sleep', labelFa: 'خواب و تغذیه' },
          { value: 'other', labelFa: 'موضوع دیگر' },
        ],
      },
      DETAILS_FIELD,
    ],
    porabzarPageKinds: ['child'],
  },
] as const;

const BY_SLUG: ReadonlyMap<string, CustomerServiceDefinition> = new Map(
  CUSTOMER_SERVICE_DEFINITIONS.map((definition) => [definition.slug, definition]),
);

export function isCustomerServiceSlug(value: unknown): value is CustomerServiceSlug {
  return typeof value === 'string' && (CUSTOMER_SERVICE_SLUGS as readonly string[]).includes(value);
}

/**
 * Look up a customer section, or `null` for an unknown slug.
 *
 * Returning `null` rather than throwing is deliberate: the slug arrives from a
 * URL or a widget attribute, so "unknown" is a normal, expected input that the
 * caller must render as an error — not an exception that becomes a 500.
 */
export function customerServiceDefinition(
  slug: string | null | undefined,
): CustomerServiceDefinition | null {
  if (!slug) return null;
  return BY_SLUG.get(slug) ?? null;
}

/** Same, but for callers that have already validated the slug. */
export function customerService(slug: CustomerServiceSlug): CustomerServiceDefinition {
  const found = BY_SLUG.get(slug);
  if (!found) throw new Error(`Unknown customer service slug: ${slug}`);
  return found;
}

/** The customer section fulfilled by a Tamoomeh domain service, if any. */
export function customerServiceForCoreSlug(
  coreSlug: string | null | undefined,
): CustomerServiceDefinition | null {
  if (!coreSlug) return null;
  return (
    CUSTOMER_SERVICE_DEFINITIONS.find((definition) => definition.coreServiceSlug === coreSlug) ??
    null
  );
}

/**
 * The discount a section's list price represents, as a whole percentage.
 *
 * Derived from the two prices rather than stored: a hand-written percentage is
 * the one number on a price block nothing checks, and «۵۰٪ تخفیف» on a
 * 1,500,000 → 1,000,000 offer would be a lie. Returns `null` when the section
 * is not on offer (no list price, or a list price that is not above the sale
 * price), so the caller renders the price alone instead of a negative discount.
 */
export function customerServiceDiscountPercent(
  definition: Pick<CustomerServiceDefinition, 'defaultAmount' | 'listAmount'>,
): number | null {
  const { listAmount, defaultAmount } = definition;
  if (typeof listAmount !== 'number' || !Number.isFinite(listAmount)) return null;
  if (listAmount <= defaultAmount) return null;
  return Math.round((1 - defaultAmount / listAmount) * 100);
}

/** The Tamoomeh domain service slug that fulfils a customer section. */
export function coreServiceSlugFor(slug: string | null | undefined): ServiceSlug | null {
  return customerServiceDefinition(slug)?.coreServiceSlug ?? null;
}

/** The customer sections that should be offered on a Porabzar page kind. */
export function customerServicesForPorabzarPageKind(
  kind: PorabzarPageKind | null | undefined,
): readonly CustomerServiceDefinition[] {
  if (!kind || kind === 'other') return [];
  return CUSTOMER_SERVICE_DEFINITIONS.filter((definition) =>
    definition.porabzarPageKinds.includes(kind),
  );
}

/**
 * Validation rules for a submitted request, derived from the field table.
 *
 * Kept here (not in the widget) so the browser and the server enforce exactly
 * the same shape from one source of truth.
 */
export interface CustomerRequestDraft {
  service: CustomerServiceSlug;
  subject: string;
  details: string;
  /** Answers to the service's `select` and `number` fields, keyed by field name. */
  answers?: Record<string, string>;
}

export interface DraftValidationIssue {
  field: string;
  messageFa: string;
}

const MAX_SUBJECT = 120;
const MAX_DETAILS = 2000;

/**
 * Fold Persian/Arabic-Indic digits (and the Arabic decimal separator) so a
 * typed «۱۷۰» validates the same as `170`. Inlined here so this catalog stays
 * free of a runtime import on `phone.ts`.
 */
function foldNumericDigits(raw: string): string {
  const persian = '۰۱۲۳۴۵۶۷۸۹';
  const arabic = '٠١٢٣٤٥٦٧٨٩';
  let out = '';
  for (const ch of raw) {
    const p = persian.indexOf(ch);
    if (p >= 0) {
      out += String(p);
      continue;
    }
    const a = arabic.indexOf(ch);
    if (a >= 0) {
      out += String(a);
      continue;
    }
    out += ch === '٫' ? '.' : ch;
  }
  return out.trim();
}

/** Is `raw` a usable number for a `number` field, and inside its bounds? */
function numberIssue(field: CustomerServiceField, raw: string): DraftValidationIssue | null {
  const value = Number(foldNumericDigits(raw));
  if (!Number.isFinite(value)) {
    return { field: field.name, messageFa: `${field.labelFa} باید یک عدد باشد` };
  }
  if (field.min !== undefined && value < field.min) {
    return { field: field.name, messageFa: `${field.labelFa} باید حداقل ${field.min} باشد` };
  }
  if (field.max !== undefined && value > field.max) {
    return { field: field.name, messageFa: `${field.labelFa} باید حداکثر ${field.max} باشد` };
  }
  return null;
}

/**
 * Validate the answers a request actually carries.
 *
 * Only values that are *present* are checked, so this is the half of the rule
 * the server can apply without knowing whether the widget asked for the field:
 * an unknown select value, a non-numeric or out-of-range number, or a body that
 * exceeds its declared ceiling is refused wherever it was submitted from. The
 * "required" half lives in `validateCustomerRequestDraft`, which the widget runs
 * before it submits.
 */
export function validateCustomerAnswerValues(
  definition: CustomerServiceDefinition,
  answers: Record<string, string> | null | undefined,
): DraftValidationIssue[] {
  const issues: DraftValidationIssue[] = [];
  const given = answers ?? {};
  for (const field of definition.fields) {
    const raw = given[field.name];
    if (raw === undefined || raw === null || raw === '') continue;
    if (field.type === 'select') {
      const allowed = field.options?.some((option) => option.value === raw) ?? false;
      if (!allowed) issues.push({ field: field.name, messageFa: `${field.labelFa} معتبر نیست` });
    } else if (field.type === 'number') {
      const issue = numberIssue(field, raw);
      if (issue) issues.push(issue);
    } else if ((field.type === 'text' || field.type === 'textarea') && field.maxLength) {
      if (raw.length > field.maxLength) {
        issues.push({
          field: field.name,
          messageFa: `${field.labelFa} حداکثر ${field.maxLength} نویسه است`,
        });
      }
    }
  }
  return issues;
}

/**
 * Validate a draft against its service definition.
 *
 * Returns every problem at once so the form can annotate all fields in a single
 * pass instead of making the customer fix them one submit at a time.
 */
export function validateCustomerRequestDraft(
  draft: Partial<CustomerRequestDraft> | null | undefined,
): DraftValidationIssue[] {
  const issues: DraftValidationIssue[] = [];
  const definition = customerServiceDefinition(draft?.service);
  if (!definition) {
    return [{ field: 'service', messageFa: 'سرویس انتخاب‌شده معتبر نیست' }];
  }

  const subject = (draft?.subject ?? '').trim();
  if (!subject) {
    issues.push({ field: 'subject', messageFa: 'موضوع درخواست را بنویسید' });
  } else if (subject.length > MAX_SUBJECT) {
    issues.push({ field: 'subject', messageFa: `موضوع حداکثر ${MAX_SUBJECT} نویسه است` });
  }

  const details = (draft?.details ?? '').trim();
  if (!details) {
    issues.push({ field: 'details', messageFa: 'توضیح درخواست را بنویسید' });
  } else if (details.length < 5) {
    issues.push({ field: 'details', messageFa: 'توضیح درخواست باید حداقل ۵ نویسه باشد' });
  } else if (details.length > MAX_DETAILS) {
    issues.push({ field: 'details', messageFa: `توضیح حداکثر ${MAX_DETAILS} نویسه است` });
  }

  // Required answers: a select must be chosen, a number must be given. A file
  // field is never required — the sport form's body photos are optional.
  for (const field of definition.fields) {
    const value = draft?.answers?.[field.name];
    if (field.required && field.type === 'select' && !value) {
      issues.push({ field: field.name, messageFa: `${field.labelFa} را انتخاب کنید` });
    }
    if (field.required && field.type === 'number' && !(value ?? '').trim()) {
      issues.push({ field: field.name, messageFa: `${field.labelFa} را وارد کنید` });
    }
  }

  // Present values, whatever their field. Reported once, so a missing required
  // select is not also reported as "invalid".
  for (const issue of validateCustomerAnswerValues(definition, draft?.answers)) {
    if (!issues.some((existing) => existing.field === issue.field)) issues.push(issue);
  }

  return issues;
}
